import React from "react";
import { chunk, Dictionary, isUndefined } from "lodash";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { SpecFormatter } from "../../../../commons/libs/formatters";
import { getBrandLogoUrl } from "../../../../commons/libs/resource-paths";
import { getFirstVariantId } from "../../../../commons/libs/specs";
import { getSpecConfig } from "../../../../commons/specs";
import { CustomBicycleFilterKey } from "../../../../commons/specs/bicycle";
import { ActiveFilters, FilterConfigItem, FilterKey } from "../../../../commons/specs/filters";
import {
  Category,
  CategoryMap,
  Product,
  ProductId,
  ProductKey,
  ProductSpecKey,
  ProductType
} from "../../../../commons/specs/product";
import { AssortmentType } from "../../../../commons/types/assortment";
import { Brand } from "../../../../commons/types/brand";
import { GlobalLocationState } from "../../../../commons/types/location";
import { ProductFinderColumnCount, ProductFinderViewMode, Settings } from "../../../../commons/types/settings";
import * as icons from "../../../../resources/icons";
import Badge from "../../components/Badge/Badge";
import BrandImage from "../../components/BrandImage/BrandImage";
import Button from "../../components/Button/Button";
import CenteredContent from "../../components/CenteredContent/CenteredContent";
import ContentTile from "../../components/ContentTile/ContentTile";
import CurrentPageBreadcrumb from "../../components/CurrentPageBreadcrumb/CurrentPageBreadcrumb";
import FilterListItem from "../../components/FilterListItem/FilterListItem";
import FlexLayout from "../../components/FlexLayout/FlexLayout";
import Headline from "../../components/Headline/Headline";
import Icon from "../../components/Icon/Icon";
import LazyContent from "../../components/LazyContent/LazyContent";
import Paragraph from "../../components/Paragraph/Paragraph";
import ProductFilterList from "../../components/ProductFilterList/ProductFilterList";
import ProductFilterListGroup from "../../components/ProductFilterList/ProductFilterListGroup";
import ProductFinderPageLayout from "../../components/ProductFinderPageLayout/ProductFinderPageLayout";
import SearchListItem from "../../components/SearchListItem/SearchListItem";
import TileGrid, { AcceptedColumnsCount } from "../../components/TileGrid/TileGrid";
import config from "../../config";
import { getFilterGroupIcon } from "../../libs/filter-icons";
import { getFilterDescription, getFilterGroupLabel, getFilterLabel } from "../../libs/filter-translations";
import { isFilterMarkedNew } from "../../libs/filters";
import { generatePath, useAppHistory, useAppLocation } from "../../libs/hooks/use-app-routing";
import useAvailabilityFilterTeaser from "../../libs/hooks/use-availability-filter-teaser";
import useCssVars from "../../libs/hooks/use-css-vars";
import useDebounce from "../../libs/hooks/use-debounce";
import { useMediaQuery } from "../../libs/hooks/use-media-query";
import useRestoreScrollPosition from "../../libs/hooks/use-restore-scroll-position";
import useSizingAvailability from "../../libs/hooks/use-sizing-availability";
import useSpecFormatter from "../../libs/hooks/use-spec-formatter";
import {
  CategorizedProduct,
  createProductListContextFromProducts,
  getGroupedProductsByView,
  GroupedProductsByBrand
} from "../../libs/products";
import { useBrands } from "../../libs/queries/use-brands";
import useEnv from "../../libs/queries/use-env";
import useProductFilter from "../../libs/queries/use-product-filter";
import useProducts from "../../libs/queries/use-products";
import { useSettings } from "../../libs/queries/use-settings";
import useVeloconnectEndpoints from "../../libs/queries/use-veloconnect-endpoints";
import { ROUTES } from "../../routes";
import { useCommonAppDispatcher } from "../../store/hooks";
import FilterListItemTooltipPartial from "../App/FilterListItemTooltipPartial";
import ModalContainerWithStatePartial from "../Modal/ModalContainerWithStatePartial";
import DelayedLoadingIndicator from "../partials/DelayedLoadingIndicator";
import { useTrackingContext } from "../utils/tracking-context";
import { VeloconnectEndpointsProvider } from "../utils/veloconnect-endpoints-context";

import AvailabilityFilterTeaserModalPartial from "./AvailabilityFilterTeaserModalPartial";
import FilterModalPartial from "./FilterModalPartial";
import ProductTilePartial from "./ProductTilePartial";

const TITLE_GRID_HEADER_HEIGHT = 68;
const GRID_GAP = 2;

type ProductFinderSidebarFilter = {
  filterConfig: FilterConfigItem<ProductType, FilterKey<ProductType>>;
  key: FilterKey<ProductType>;
};

interface ProductFinderRendererProps<Type extends ProductType> {
  brandsMap: Dictionary<Brand<ProductType>>;
  highlightedBrandKeys: string[];
  isFetching: boolean;
  products: Product<Type>[];
  productType: ProductType;
  brandKey?: string;
  searchTerm: string;
  viewMode: ProductFinderViewMode;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  activeFilters: ActiveFilters<Type>;
  settings: Settings;
  specFormatter: SpecFormatter<Type>;
  columnCount: ProductFinderColumnCount;
}

const ProductFinderRenderer = React.memo(
  <Type extends ProductType>({
    brandsMap,
    highlightedBrandKeys,
    isFetching,
    products,
    productType,
    brandKey,
    searchTerm,
    viewMode,
    scrollRef,
    activeFilters,
    settings,
    specFormatter,
    columnCount
  }: ProductFinderRendererProps<Type>) => {
    const { t } = useTranslation(["commons"]);
    const breakpoints = useMediaQuery();
    const columnCountMinusOne = { 3: 2, 4: 3 } as const; // For viewports smaller than xl (`as const` needed to match AcceptedColumnsCount)
    const gridColumns = breakpoints.xl ? columnCount : columnCountMinusOne[columnCount];

    const cssVars = useCssVars();
    const productTileHeight = parseInt(
      !brandKey ? cssVars["product-tile-height-no-brand"] : cssVars["product-tile-height"],
      10
    );

    const onPushAndStoreScrollPosition = useRestoreScrollPosition(scrollRef, products.length > 0);

    const goToProduct = (dataSource: Product[] | GroupedProductsByBrand, id: ProductId, variantId?: ProductId) => {
      const state: GlobalLocationState = {
        backLabel: t("commons:productFinderPartialProductFinder.backToProductFinderButton"),
        productListContext: createProductListContextFromProducts(dataSource),
        variantId
      };
      onPushAndStoreScrollPosition(generatePath(ROUTES.PRODUCT_DETAILS.INDEX, { id: String(id) }), state);
    };

    const renderUngroupedView = (products: Product<Type>[]) => {
      const gridRows = 2;

      return (
        <div>
          {chunk(products, gridColumns * gridRows).map((chunk, index) => (
            <LazyContent
              // Performance: Stable key prevents reconciliation issues, unmounting prevents DOM accumulation
              key={chunk[0]?.[ProductKey.ProductId] ?? index}
              height={
                Math.ceil(chunk.length / gridColumns) * productTileHeight +
                (Math.ceil(chunk.length / gridColumns) - 1) * GRID_GAP
              }
            >
              {({ isVisible }) =>
                isVisible /* Space-top modifier is needed to keep space between TileGrids in sync with space between TileGrid rows. */ ? (
                  <TileGrid
                    columns={gridColumns satisfies AcceptedColumnsCount}
                    fill
                    classNames={[index > 0 ? "u-space-top-xxxs" : ""]}
                  >
                    {chunk.map(product => (
                      <ProductTilePartial<Type>
                        key={product[ProductKey.ProductId]}
                        product={product}
                        brandKey={brandKey}
                        activeFilters={activeFilters}
                        onClick={() => goToProduct(products, product[ProductKey.ProductId], getFirstVariantId(product))}
                        breakpoints={breakpoints}
                        customization={settings.customization}
                        currency={settings.currency}
                        assortmentType={settings.assortment.type}
                        assortmentPriceSettings={settings.assortment.priceSettings}
                        specFormatter={specFormatter}
                      />
                    ))}
                  </TileGrid>
                ) : null
              }
            </LazyContent>
          ))}
        </div>
      );
    };

    const renderGroupedView = (productType: ProductType, groupedProducts: GroupedProductsByBrand) =>
      Object.entries(groupedProducts).map(([currentBrandKey, categories]) => (
        <React.Fragment key={currentBrandKey}>
          {!brandKey && brandsMap[currentBrandKey] && (
            <ContentTile>
              <FlexLayout gap="s" justifyContent="center" alignItems="center">
                {brandsMap[currentBrandKey].highlighted ? (
                  <Icon source={icons.IconMediumHighlight} size="l" />
                ) : undefined}
                <BrandImage position="center" src={getBrandLogoUrl(brandsMap[currentBrandKey].key)} />
              </FlexLayout>
            </ContentTile>
          )}
          {(Object.entries(categories) as [Category, CategorizedProduct<Type>][]).map(
            ([categoryKey, categorizedProduct]) =>
              Object.entries(categorizedProduct)
                .filter(([, categoryProducts]) => categoryProducts.length !== 0)
                .map(([engineKey, categoryProducts]) => (
                  <LazyContent
                    key={`${currentBrandKey}-${categoryKey}-${engineKey}`}
                    height={
                      TITLE_GRID_HEADER_HEIGHT +
                      Math.ceil(categoryProducts.length / gridColumns) * productTileHeight +
                      (Math.ceil(categoryProducts.length / gridColumns) - 1) * GRID_GAP
                    }
                  >
                    {({ isVisible }) => {
                      return (
                        isVisible && (
                          <TileGrid
                            columns={gridColumns}
                            key={`${currentBrandKey}-${categoryKey}-${engineKey}`}
                            stickyHeader
                            fill
                            header={
                              <>
                                {brandKey && brandsMap[currentBrandKey]?.highlighted ? (
                                  <Icon source={icons.IconSmallHighlight} />
                                ) : undefined}
                                <Headline kind={breakpoints.l ? "m" : "base"}>
                                  {brandsMap[currentBrandKey] && brandsMap[currentBrandKey].displayName}{" "}
                                  {/* Limitation of TypeScript: https://github.com/microsoft/TypeScript/issues/57388 */}
                                  {t(
                                    `commons:specs.${productType}.categories.${categoryKey}.${engineKey === "withEngine" ? "withEngine" : "default"}` as keyof {
                                      [
                                        T in ProductType as `commons:specs.${T}.categories.${CategoryMap[T]}.${T extends ProductType.Bicycle ? "default" | "withEngine" : "default"}`
                                      ]: null;
                                    }
                                  )}
                                </Headline>
                              </>
                            }
                          >
                            {categoryProducts.map(product => (
                              <ProductTilePartial<Type>
                                key={product[ProductKey.ProductId]}
                                product={product}
                                activeFilters={activeFilters}
                                brandKey={brandKey}
                                onClick={() =>
                                  goToProduct(
                                    groupedProducts,
                                    product[ProductKey.ProductId],
                                    getFirstVariantId(product)
                                  )
                                }
                                breakpoints={breakpoints}
                                customization={settings.customization}
                                currency={settings.currency}
                                assortmentType={settings.assortment.type}
                                assortmentPriceSettings={settings.assortment.priceSettings}
                                specFormatter={specFormatter}
                              />
                            ))}
                          </TileGrid>
                        )
                      );
                    }}
                  </LazyContent>
                ))
          )}
        </React.Fragment>
      ));

    if (isFetching) {
      return (
        <CenteredContent>
          <DelayedLoadingIndicator />
        </CenteredContent>
      );
    } else {
      if (products.length === 0) {
        return (
          <CenteredContent>
            <Paragraph size="l" classNames={["u-text-center"]}>
              {t("commons:productFinderPartialProductFinder.noResultsFound")}
            </Paragraph>
          </CenteredContent>
        );
      }

      if (searchTerm) {
        return renderUngroupedView(products);
      }

      return renderGroupedView(productType, getGroupedProductsByView(products, viewMode, highlightedBrandKeys));
    }
  }
);

ProductFinderRenderer.displayName = "ProductFinderRenderer";

interface Props {
  backButton: () => React.ReactNode;
  isOffline: boolean;
}

const ProductFinderPartial = ({ backButton, isOffline }: Props) => {
  const [dispatch, actions] = useCommonAppDispatcher();
  const history = useAppHistory();
  const { t, i18n } = useTranslation(["commons"]);

  const env = useEnv();
  const settings = useSettings();
  const brands = useBrands();

  const specConfig = getSpecConfig(env.productType);
  const specFormatter = useSpecFormatter("commons", env.productType, settings.initialized.customization);

  const scrollRef = React.useRef<HTMLDivElement>(null);
  const sidebarScrollRef = React.useRef<HTMLDivElement>(null);

  const { brand: brandKey } = useParams<{ brand?: string }>();
  const brand = brandKey ? brands.getBrand(brandKey) : undefined;

  const { mixpanel } = useTrackingContext();
  const breakpoints = useMediaQuery();

  const location = useAppLocation();

  const [searchTerm, setSearchTerm] = React.useState(location?.state?.searchTerm ?? "");
  const debouncedSearchTerm = useDebounce(searchTerm, config.shared.filter.filterFetchDelay);

  const veloconnectEndpoints = useVeloconnectEndpoints();

  const [isAnyFilterModalOpen, setIsAnyFilterModalOpen] = React.useState(false);

  const productFilter = useProductFilter({
    productType: env.productType,
    assortmentFilterSettings: settings.assortmentFilterSettings,
    brandKey,
    skipDefaultActiveFilters: location.state?.forceFilters
  });

  const products = useProducts({
    productType: env.productType,
    activeFilters: productFilter.activeFilters,
    searchTerm: debouncedSearchTerm,
    brandKey,
    options: {
      enabled: productFilter.wereDefaultActiveFiltersEvaluated
    }
  });

  const isFetching = products.query.isPending || products.query.isFetching || productFilter.query.isFetching;

  React.useEffect(() => {
    if (productFilter.query.error) {
      dispatch(actions.error.set(productFilter.query.error));
    }
  }, [productFilter.query.error, dispatch, actions.error]);

  const viewMode = isUndefined(brandKey)
    ? settings.initialized.customization.productFinderViewMode
    : ProductFinderViewMode.GroupedByBrandAndCategory;

  React.useEffect(() => {
    const nextLocationState: GlobalLocationState = {
      ...(location.state ?? {}),
      activeFilters: productFilter.activeFilters,
      searchTerm: debouncedSearchTerm
    };

    if (productFilter.wereDefaultActiveFiltersEvaluated) {
      delete nextLocationState.forceFilters;
    }

    const nextPath = [location.pathname, location.search, location.hash].join("");

    // This doubles the number of renders in this partial and even in ProductFinderRenderer. But there seems to be no way to prevent this.
    history.replace(nextPath, nextLocationState);
    // Only update the location when the listed deps change otherwise the effect will trigger indefinitely.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, productFilter.activeFilters, productFilter.wereDefaultActiveFiltersEvaluated]);

  React.useEffect(() => {
    const hasActiveFiltersOrSearchTerm =
      Object.keys(productFilter.activeFilters ?? {}).length > 0 || debouncedSearchTerm.length > 0;

    if (hasActiveFiltersOrSearchTerm) {
      mixpanel?.trackFilterChanged(productFilter.activeFilters, debouncedSearchTerm);
    }
  }, [productFilter.activeFilters, debouncedSearchTerm, mixpanel]);

  const onClearAllFiltersAndSearchTermAndBodyValues = () => {
    productFilter.clearAllFilters();
    setSearchTerm("");
  };

  const sizingAvailability = useSizingAvailability({ isOnline: !isOffline });
  const availabilityFilterTeaser = useAvailabilityFilterTeaser();

  const isSidebarFilterVisible = (filterKey: FilterKey<ProductType>) =>
    (filterKey !== ProductSpecKey.ManualAssortment || settings.initialized.assortment.type === AssortmentType.Manual) &&
    (filterKey !== ProductSpecKey.CombinedAssortment ||
      availabilityFilterTeaser.isVisible ||
      (settings.initialized.assortment.type === AssortmentType.Automatic &&
        !settings.assortmentFilterSettings.hideAutomaticAssortmentFilter)) &&
    (filterKey !== ProductSpecKey.BrandKey || !brandKey) &&
    (filterKey !== ProductSpecKey.Price || settings.initialized.customization.showPrices) &&
    ((filterKey as FilterKey<ProductType.Bicycle>) !== CustomBicycleFilterKey.Sizing ||
      settings.isBodySizingEnabled ||
      settings.isBodySizingNotificationsEnabled);

  const getSidebarFilters = (filterKeys: FilterKey<ProductType>[]): ProductFinderSidebarFilter[] =>
    filterKeys.filter(isSidebarFilterVisible).flatMap(key => {
      const filterConfig = specConfig.filterConfig[key] as ProductFinderSidebarFilter["filterConfig"] | undefined;

      return filterConfig ? [{ key, filterConfig }] : [];
    });

  const sidebarFilterGroups = specConfig.filterGroups
    .map(group => ({
      key: group.key,
      filters: getSidebarFilters(group.filters as FilterKey<ProductType>[])
    }))
    .filter(group => group.filters.length > 0);

  const isSidebarFilterActive = ({ key }: ProductFinderSidebarFilter) => productFilter.isFilterItemActive(key) > 0;

  const isSidebarFilterGroupActive = (filters: ProductFinderSidebarFilter[]) => filters.some(isSidebarFilterActive);

  const [openSidebarFilterGroups, setOpenSidebarFilterGroups] = React.useState<Record<string, boolean>>(() =>
    sidebarFilterGroups.reduce(
      (openGroups, group) => ({
        ...openGroups,
        [group.key]: group.key === "general" || isSidebarFilterGroupActive(group.filters)
      }),
      {}
    )
  );

  const isSidebarFilterGroupOpen = (groupKey: string) => openSidebarFilterGroups[groupKey] ?? groupKey === "general";

  const toggleSidebarFilterGroup = (groupKey: string) => {
    setOpenSidebarFilterGroups(openGroups => ({
      ...openGroups,
      [groupKey]: !(openGroups[groupKey] ?? groupKey === "general")
    }));
  };

  const renderSidebarFilter = ({ filterConfig, key }: ProductFinderSidebarFilter) => (
    <ModalContainerWithStatePartial
      animationDuration={config.shared.transitionsDurations.fast}
      key={key}
      hideBackground={breakpoints.xl}
      position={
        // Since the `AvailabilityFilterModal` does expand and shrink its height,
        // we want to prevent too much layout shifting by using a different position for the `ModalContainer`
        // which keeps the top of the FilterModal in the top right but can expand to the bottom.
        breakpoints.xl && key === ProductSpecKey.CombinedAssortment
          ? settings.initialized.customization.productFinderSidebarPosition === "left"
            ? "top-left"
            : "top-right"
          : breakpoints.xl
            ? settings.initialized.customization.productFinderSidebarPosition
            : "center"
      }
      onOpenChange={isOpen => {
        if (isOpen) {
          mixpanel?.trackFilterModalOpened(key);
        } else {
          mixpanel?.trackFilterModalClosed(key);
        }
      }}
      modal={(isOpen, close) => (
        <FilterModalPartial
          isFetching={products.query.isFetching}
          isOpen={isOpen}
          // `align-${alignPosition}` reserves box-shadow space for edge-anchored modals; must be omitted
          // when `position` above resolves to "center", otherwise it shifts the centered modal off-center.
          alignPosition={breakpoints.xl ? settings.initialized.customization.productFinderSidebarPosition : undefined}
          productType={env.productType}
          filterKey={key}
          filterConfig={filterConfig}
          filterValues={productFilter.query.data ?? {}}
          activeFilters={productFilter.activeFilters}
          variantsLength={products.variantsLength}
          onFilterChange={productFilter.setActiveFilters}
          highlightedBrandKeys={brands.highlightedKeys}
          onClose={close}
          onOpenChange={setIsAnyFilterModalOpen}
        />
      )}
    >
      {(_isOpen, open) => (
        <FilterListItemTooltipPartial
          filterKey={key}
          filterConfig={filterConfig}
          filterValues={productFilter.query.data ?? {}}
          selectedBrands={productFilter.selectedBrands}
          activeFilters={productFilter.activeFilters}
          filterKeysWithInsufficientFilterValues={productFilter.filterKeysWithInsufficientFilterValues}
          label={getFilterLabel(t, env.productType, key, settings.initialized.customization.modelYearDisplay.term)}
          description={getFilterDescription(t, i18n, env.productType, key)}
          active={productFilter.isFilterItemActive(key)}
          badge={
            isFilterMarkedNew(filterConfig) ? <Badge size="s" variant="accent" label={t("defaults.new")} /> : undefined
          }
          variantsLength={products.variantsLength}
          isOffline={isOffline}
          preventTooltip={isAnyFilterModalOpen}
          scrollRef={sidebarScrollRef}
          onClick={open}
          onRemove={() => {
            mixpanel?.trackFilterCleared(key);
            productFilter.clearFilter(key);
          }}
          disabledReason={
            (key as FilterKey<ProductType.Bicycle>) === CustomBicycleFilterKey.Sizing &&
            settings.isBodySizingEnabled &&
            !sizingAvailability.available
              ? t("commons:productFinderPartialProductFinder.sizingServiceUnavailable")
              : undefined
          }
        />
      )}
    </ModalContainerWithStatePartial>
  );

  const renderAvailabilityFilterTeaser = ({ key }: ProductFinderSidebarFilter) => {
    const label = getFilterLabel(t, env.productType, key, settings.initialized.customization.modelYearDisplay.term);

    return (
      <ModalContainerWithStatePartial
        animationDuration={config.shared.transitionsDurations.fast}
        key={key}
        hideBackground={breakpoints.xl}
        position={breakpoints.xl ? settings.initialized.customization.productFinderSidebarPosition : "center"}
        onOpenChange={isOpen => {
          setIsAnyFilterModalOpen(isOpen);
          if (isOpen) {
            mixpanel?.trackAvailabilityFilterTeaserOpened();
          }
        }}
        modal={(_isOpen, close) => (
          <AvailabilityFilterTeaserModalPartial
            alignPosition={breakpoints.xl ? settings.initialized.customization.productFinderSidebarPosition : undefined}
            title={label}
            hasCallToAction={availabilityFilterTeaser.hasCallToAction}
            onClose={close}
            onDismiss={availabilityFilterTeaser.dismiss}
          />
        )}
      >
        {(_isOpen, open) => (
          <FilterListItem
            label={label}
            badge={<Badge size="s" variant="accent" label={t("defaults.new")} />}
            onClick={open}
          />
        )}
      </ModalContainerWithStatePartial>
    );
  };

  const renderSidebar = () => (
    <ProductFilterList
      scrollRef={sidebarScrollRef}
      header={
        <SearchListItem
          placeholder={t("commons:productFinderPartialProductFinder.searchForPlaceholder")}
          value={searchTerm}
          onChange={setSearchTerm}
        />
      }
      footer={
        <>
          <Button
            size="s"
            block
            variant="standout"
            icon={<Icon source={icons.IconSmallBin} />}
            disabled={!productFilter.hasActiveFilters && searchTerm.length === 0}
            onClick={onClearAllFiltersAndSearchTermAndBodyValues}
          >
            {breakpoints.l
              ? t("commons:productFinderPartialProductFinder.deleteAllFiltersButton")
              : t("commons:productFinderPartialProductFinder.deleteFiltersButton")}
          </Button>
          <Paragraph kind="support" size={!breakpoints.xl ? "s" : "base"}>
            {isFetching
              ? t("commons:productFinderPartialProductFinder.loading")
              : t("commons:productFinderPartialProductFinder.foundModels", {
                  count: products.query.data?.totalFiltered ?? 0
                })}
          </Paragraph>
        </>
      }
    >
      {sidebarFilterGroups.map(group => {
        const open = isSidebarFilterGroupOpen(group.key);

        return (
          <ProductFilterListGroup
            active={isSidebarFilterGroupActive(group.filters)}
            icon={getFilterGroupIcon(env.productType, group.key)}
            key={group.key}
            label={getFilterGroupLabel(t, env.productType, group.key)}
            onToggle={() => toggleSidebarFilterGroup(group.key)}
            open={open}
          >
            {group.filters.map(filter =>
              filter.key === ProductSpecKey.CombinedAssortment && availabilityFilterTeaser.isVisible
                ? renderAvailabilityFilterTeaser(filter)
                : renderSidebarFilter(filter)
            )}
          </ProductFilterListGroup>
        );
      })}
    </ProductFilterList>
  );

  return (
    <VeloconnectEndpointsProvider endpoints={veloconnectEndpoints}>
      <ProductFinderPageLayout
        scrollRef={scrollRef}
        headerLeft={backButton()}
        headerRight={
          brandKey ? (
            <FlexLayout alignItems="center" gap="sl" classNames={["u-space-top-l"]}>
              {brand?.highlighted ? <Icon source={icons.IconMediumHighlight} size="l" /> : undefined}
              <BrandImage position="right" src={getBrandLogoUrl(brandKey)} />
            </FlexLayout>
          ) : (
            <CurrentPageBreadcrumb>
              {t("commons:productFinderPartialProductFinder.bikeFinderLabel")}
            </CurrentPageBreadcrumb>
          )
        }
        aside={renderSidebar()}
        asidePosition={settings.initialized.customization.productFinderSidebarPosition}
      >
        {specFormatter ? (
          <ProductFinderRenderer
            brandsMap={brands.brandsMap}
            highlightedBrandKeys={brands.highlightedKeys}
            products={products.query.data?.results ?? []}
            isFetching={isFetching}
            activeFilters={productFilter.activeFilters}
            productType={env.productType}
            brandKey={brandKey}
            searchTerm={products.usedSearchTerm}
            viewMode={viewMode}
            scrollRef={scrollRef}
            settings={settings.initialized}
            specFormatter={specFormatter}
            columnCount={settings.initialized.customization.productFinderColumnCount}
          />
        ) : null}
      </ProductFinderPageLayout>
    </VeloconnectEndpointsProvider>
  );
};

export default ProductFinderPartial;
